import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Form, InputGroup, ListGroup, Spinner } from "react-bootstrap";
import { FiSearch, FiCheck } from "react-icons/fi";

export interface SearchableOption {
    value: string | number;
    label: string;
    sublabel?: string;
    /** Non-selectable section header; options sharing a group render under one header. */
    group?: string;
}

interface SearchableListPickerProps {
    options: SearchableOption[];
    value?: string | number | null;
    onChange: (value: string | number) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    disabled?: boolean;
    loading?: boolean;
    isInvalid?: boolean;
    maxHeight?: number;
    emptyText?: string;
    testId?: string;
    /**
     * When true (default), filter options locally by label/sublabel.
     * When false, show options as provided and call onSearchChange for remote search.
     */
    filterLocally?: boolean;
    /** Fired when the search box changes (useful for async / remote search). */
    onSearchChange?: (query: string) => void;
    /** Below this length (when filterLocally is false), show minQueryHint instead of options. */
    minQueryLength?: number;
    /** Shown when query is shorter than minQueryLength in async mode. */
    minQueryHint?: string;
    /** Optional label shown on the closed toggle when value is set but not in options (e.g. after async clear). */
    selectedLabel?: string;
}

type ListEntry =
    | { kind: "header"; key: string; label: string }
    | { kind: "divider"; key: string }
    | { kind: "option"; option: SearchableOption };

/** Above Bootstrap modal (~1050–1055) so portaled menus stay visible. */
const MENU_Z_INDEX = 1060;
const MENU_GAP_PX = 4;

/**
 * Portal into the nearest Bootstrap modal root when present so enforceFocus
 * still treats the menu as inside the dialog; otherwise use document.body.
 */
export function resolveMenuPortalRoot(anchor: Element | null): HTMLElement {
    const modal = anchor?.closest(".modal");
    return modal instanceof HTMLElement ? modal : document.body;
}

function buildGroupedEntries(options: SearchableOption[]): ListEntry[] {
    const entries: ListEntry[] = [];
    let lastGroup: string | undefined;
    let groupIndex = 0;

    for (const option of options) {
        const group = option.group;
        if (group && group !== lastGroup) {
            if (lastGroup !== undefined) {
                entries.push({ kind: "divider", key: `divider-${groupIndex}` });
            }
            entries.push({ kind: "header", key: `header-${groupIndex}-${group}`, label: group });
            lastGroup = group;
            groupIndex += 1;
        } else if (!group && lastGroup !== undefined) {
            entries.push({ kind: "divider", key: `divider-${groupIndex}` });
            lastGroup = undefined;
            groupIndex += 1;
        }
        entries.push({ kind: "option", option });
    }

    return entries;
}

interface MenuPosition {
    top: number;
    left: number;
    width: number;
    maxListHeight: number;
    openAbove: boolean;
}

/**
 * A single-select, searchable dropdown. The menu is portaled to document.body so
 * modal/overflow ancestors cannot clip it (same idea as ChildMediaPopover).
 */
const SearchableListPicker: React.FC<SearchableListPickerProps> = ({
    options,
    value,
    onChange,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    disabled = false,
    loading = false,
    isInvalid = false,
    maxHeight = 280,
    emptyText = "No matches found",
    testId = "searchable-list-picker",
    filterLocally = true,
    onSearchChange,
    minQueryLength = 0,
    minQueryHint,
    selectedLabel,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const onSearchChangeRef = useRef(onSearchChange);
    onSearchChangeRef.current = onSearchChange;

    const selected = useMemo(
        () => options.find((o) => String(o.value) === String(value)),
        [options, value]
    );

    const displayLabel = selected?.label ?? selectedLabel;

    const filtered = useMemo(() => {
        if (!filterLocally) return options;
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter(
            (o) =>
                o.label.toLowerCase().includes(q) ||
                (o.sublabel ? o.sublabel.toLowerCase().includes(q) : false)
        );
    }, [options, query, filterLocally]);

    const queryTooShort =
        !filterLocally && minQueryLength > 0 && query.trim().length < minQueryLength;

    const listEntries = useMemo(
        () => (queryTooShort ? [] : buildGroupedEntries(filtered)),
        [filtered, queryTooShort]
    );

    const updateMenuPosition = useCallback(() => {
        const toggle = toggleRef.current;
        if (!toggle) return;
        const rect = toggle.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP_PX;
        const spaceAbove = rect.top - MENU_GAP_PX;
        const openAbove = spaceBelow < Math.min(maxHeight + 56, 200) && spaceAbove > spaceBelow;
        const available = openAbove ? spaceAbove : spaceBelow;
        // Reserve room for the search InputGroup (~40px) + padding.
        const maxListHeight = Math.max(80, Math.min(maxHeight, available - 48));

        setMenuPosition({
            top: openAbove ? rect.top - MENU_GAP_PX : rect.bottom + MENU_GAP_PX,
            left: rect.left,
            width: rect.width,
            maxListHeight,
            openAbove,
        });
    }, [maxHeight]);

    useLayoutEffect(() => {
        if (!open) {
            setMenuPosition(null);
            return;
        }
        updateMenuPosition();
        window.addEventListener("resize", updateMenuPosition);
        window.addEventListener("scroll", updateMenuPosition, true);
        return () => {
            window.removeEventListener("resize", updateMenuPosition);
            window.removeEventListener("scroll", updateMenuPosition, true);
        };
    }, [open, updateMenuPosition, filtered.length, loading, queryTooShort]);

    // Close on outside click (toggle + portaled menu are both "inside").
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            const target = e.target as Node;
            if (containerRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    // Focus the search field when opening.
    useEffect(() => {
        if (open) {
            setQuery("");
            onSearchChangeRef.current?.("");
            const t = setTimeout(() => searchRef.current?.focus(), 0);
            return () => clearTimeout(t);
        }
    }, [open]);

    const handleQueryChange = (next: string) => {
        setQuery(next);
        onSearchChangeRef.current?.(next);
    };

    const menuStyle: React.CSSProperties | undefined = menuPosition
        ? {
              position: "fixed",
              zIndex: MENU_Z_INDEX,
              left: menuPosition.left,
              width: menuPosition.width,
              ...(menuPosition.openAbove
                  ? { bottom: window.innerHeight - menuPosition.top, top: "auto" }
                  : { top: menuPosition.top }),
          }
        : undefined;

    const menu = open && menuPosition && (
        <div
            ref={menuRef}
            className="border rounded shadow-sm bg-body p-2"
            style={menuStyle}
            data-testid={`${testId}-menu`}
        >
            <InputGroup size="sm" className="mb-2">
                <InputGroup.Text>
                    <FiSearch />
                </InputGroup.Text>
                <Form.Control
                    ref={searchRef as any}
                    placeholder={searchPlaceholder}
                    value={query}
                    onChange={(e) => handleQueryChange(e.target.value)}
                    data-testid={`${testId}-search`}
                />
            </InputGroup>

            <ListGroup
                variant="flush"
                style={{ maxHeight: menuPosition.maxListHeight, overflowY: "auto" }}
                data-testid={`${testId}-list`}
            >
                {loading && (
                    <div className="text-center text-muted small py-3">
                        <Spinner size="sm" animation="border" className="me-2" />
                        Loading…
                    </div>
                )}

                {!loading && queryTooShort && (
                    <div className="text-muted small p-3" data-testid={`${testId}-min-query-hint`}>
                        {minQueryHint || `Type at least ${minQueryLength} characters to search.`}
                    </div>
                )}

                {!loading && !queryTooShort && filtered.length === 0 && (
                    <div className="text-muted small p-3">{emptyText}</div>
                )}

                {!loading &&
                    !queryTooShort &&
                    listEntries.map((entry) => {
                        if (entry.kind === "header") {
                            return (
                                <div
                                    key={entry.key}
                                    className="text-muted text-uppercase small fw-semibold px-3 pt-2 pb-1"
                                    data-testid={`${testId}-group-${entry.label}`}
                                >
                                    {entry.label}
                                </div>
                            );
                        }
                        if (entry.kind === "divider") {
                            return <hr key={entry.key} className="my-1 mx-2" />;
                        }
                        const o = entry.option;
                        const isSelected = String(o.value) === String(value);
                        return (
                            <ListGroup.Item
                                key={String(o.value)}
                                action
                                as="button"
                                type="button"
                                active={isSelected}
                                onClick={() => {
                                    onChange(o.value);
                                    setOpen(false);
                                }}
                                className="d-flex align-items-center justify-content-between"
                                data-testid={`${testId}-option-${o.value}`}
                            >
                                <span>
                                    <span className="fw-semibold">{o.label}</span>
                                    {o.sublabel && (
                                        <span className="text-muted small ms-2">{o.sublabel}</span>
                                    )}
                                </span>
                                {isSelected && <FiCheck aria-hidden />}
                            </ListGroup.Item>
                        );
                    })}
            </ListGroup>
        </div>
    );

    return (
        <div ref={containerRef} style={{ position: "relative" }} data-testid={testId}>
            <button
                ref={toggleRef}
                type="button"
                disabled={disabled || loading}
                onClick={() => !disabled && !loading && setOpen((o) => !o)}
                className={`form-select text-start d-flex align-items-center justify-content-between ${isInvalid ? "is-invalid" : ""}`}
                title={displayLabel || undefined}
                data-testid={`${testId}-toggle`}
            >
                <span
                    className={displayLabel ? "" : "text-muted"}
                    style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
                >
                    {loading && !displayLabel ? "Loading…" : displayLabel || placeholder}
                </span>
            </button>

            {menu
                ? createPortal(menu, resolveMenuPortalRoot(containerRef.current ?? toggleRef.current))
                : null}
        </div>
    );
};

export default SearchableListPicker;
